import type { EventName } from './types';

/**
 * GTM is loaded via the official `@analytics/google-tag-manager` plugin, which
 * pushes EVERY `track`/`page` call to `window.dataLayer`. We don't want the
 * whole funnel (or any identify traits) reaching third-party tags — only a
 * curated set of conversion events. The `analytics` core supports per-call
 * destination gating via `options.plugins` (see getanalytics.io and the same
 * pattern in `icasino-player-fe`), so this module is the single choke point
 * that decides, per event, whether the GTM plugin is included.
 *
 * To send a new event to GTM: add its name here. To send something to GTM
 * ONLY, pass `{ plugins: { all: false, [GTM_PLUGIN_NAME]: true } }` at the
 * call site. The growth team maps these raw event names → GA4/Ads params
 * inside the GTM UI.
 */
export const GTM_PLUGIN_NAME = 'google-tag-manager';

/**
 * Conversion events allowed to reach the GTM dataLayer. Product funnel
 * conversions were removed; only the baseline `page_viewed` remains. Add a
 * new conversion event's name here to let it reach GTM.
 */
export const GTM_EVENTS: ReadonlySet<EventName> = new Set<EventName>(['page_viewed']);

export interface PluginGateOptions {
  plugins: Record<string, boolean>;
}

/** Always-exclude-GTM options — for identify/peopleSet so PII never enters the dataLayer. */
export const EXCLUDE_GTM: PluginGateOptions = { plugins: { [GTM_PLUGIN_NAME]: false } };

/**
 * `analytics` track options for an event: `undefined` (send everywhere,
 * including GTM) when the event is an allowlisted conversion, otherwise
 * options that exclude only the GTM plugin (Mixpanel still receives it).
 */
export function gtmOptionsFor(name: EventName): PluginGateOptions | undefined {
  return GTM_EVENTS.has(name) ? undefined : EXCLUDE_GTM;
}
